import axios from "axios";
import * as Sentry from "@sentry/react";
import { attachSentryResponseInterceptor } from "./sentryAxiosInterceptor";
import {
  InvestmentFundsOperable,
  InvestmentsFundsClasses,
  InvestmentFundsOperableWithID,
  Symbol,
  SymbolStaticData,
  SymbolType,
  SymbolVersionsResponse,
} from "../types/types";

//TYPES
import { AuthData } from "../types/types";

//AXIOS INSTANCE

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_MARKET_DATA_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const localAuth = localStorage.getItem("allaria-auth");

    const parsedLocalAuth: AuthData | null =
      (localAuth && JSON.parse(localAuth)) ?? null;

    const token = parsedLocalAuth?.access_token;

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);
attachSentryResponseInterceptor(axiosInstance);

export const getAllSymbols = async () => {
  const response = await axiosInstance.get(`/securities/symbols/all`);
  return response.data as Promise<Symbol[]>;
};

export const getInvestmentsFundsOperable = async () => {
  const response = await axiosInstance.get(
    `/investment-funds/classes/operable`,
  );
  return response.data as Promise<InvestmentFundsOperable[]>;
};

export const getInvestmentsFundsClasses = async () => {
  const response = await axiosInstance.get(`/investment-funds/operable`);
  return response.data as Promise<InvestmentsFundsClasses[]>;
};

/**
 * @deprecated
 * Use `getInstrumentStaticData`, `getOperablesSymbolsData` and
 * `getSymbolStaticData` instead.
 */
export const getAllSymbolsWithStaticData = async () => {
  const response = await axiosInstance.get<SymbolStaticData[]>(`/static-data`);
  return response.data;
};

export const getInstrumentStaticData = async ({
  type,
}: {
  type: SymbolType;
}) => {
  const response = await axiosInstance.get<Promise<SymbolStaticData[]>>(
    `/static-data/${type}`,
  );
  if (!Array.isArray(response.data)) {
    Sentry.captureMessage(
      `getInstrumentStaticData: unexpected non-array response for type "${type}"`,
      {
        level: "error",
        extra: { type, responseData: response.data },
      },
    );
  }
  return response.data;
};

export const getAllSymbolVersions = async ({
  symbol,
  allowWithoutVolume = true,
}: {
  symbol: string;
  allowWithoutVolume?: boolean;
}) => {
  return (
    await axiosInstance.get<SymbolVersionsResponse>(
      `/static-data/symbols/get-all-versions/${symbol}`,
      { params: { allow_without_volume: !!allowWithoutVolume } },
    )
  ).data;
};

export const getOperablesSymbolsData = async () => {
  const response = await axiosInstance.get(`/securities/symbols/list-operable`);
  return response.data as Promise<SymbolStaticData[]>;
};

export const getSymbolStaticData = async ({ ticker }: { ticker: string }) => {
  const response = await axiosInstance.get<SymbolStaticData>(
    `/static-data/symbol/${ticker}`,
  );
  return response.data;
};

export const getInvestmentsFundsOperableByID = async (id: number) => {
  const response = await axiosInstance.get(`/investment-funds/operable/${id}`);
  return response.data as Promise<InvestmentFundsOperableWithID>;
};

export const getInvestmentsFundsNoOperableByID = async ({
  id,
}: {
  id: number;
}) => {
  const response = await axiosInstance.get(`/investment-funds/id/${id}`);
  return response.data as Promise<InvestmentFundsOperableWithID>;
};

export const getFavorites = async (accountNumber: string) => {
  const response = await axiosInstance.get(`/favorites/${accountNumber}`);
  return response.data;
};

export const postFavorites = async (accountNumber: string, symbol: string) => {
  const response = await axiosInstance.post(`/favorites/${accountNumber}`, {
    symbol,
  });
  return response.data;
};

export const deleteFavorites = async (
  accountNumber: string,
  symbol: string,
) => {
  const response = await axiosInstance.delete(
    `/favorites/${accountNumber}/${symbol}`,
    {
      data: {},
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    },
  );
  return response.data;
};

export const getRepoOperations = async () => {
  const response = await axiosInstance.get(
    "/securities/repo-operation-at-close",
  );
  return response.data;
};
